import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Search from '@mui/icons-material/Search';
import { Field } from 'redux-form';
import { Button } from '@/ui';
import { useOfferBlockByOfferId } from '../../../OfferBlocks/queries';
import { renderComboBox } from '../../../../components/FormFields/reduxFormRenderers';

import { filterContainersByOfferType } from '../sequenceUtils';
import { DESTINATION_OFFER_TYPE_NAME } from '../constants';
import OfferVariationsCard from './OfferVariationsCard';

const LABEL_SX = { display: 'block', mb: 0.5 };

/** One anchor styled as a button, rather than a button nested inside an anchor. */
const VisitLink = ({ to, title }) => (
  <Tooltip describeChild title={title}>
    <Button
      component={Link}
      to={to}
      target='_blank'
      rel='noopener noreferrer'
      data-external-link='true'
      color='info'
      aria-label={title}
      sx={{ minWidth: 0, px: 1 }}
    >
      <Search fontSize='small' />
    </Button>
  </Tooltip>
);

/** Heading row: the node's name with a link out to wherever it is really configured. */
const PropertiesHeader = ({ name, subtitle, children }) => (
  <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
    <div>
      <Typography variant='h5' component='h3'>
        {name}
      </Typography>
      {subtitle && <Typography variant='subtitle2'>{subtitle}</Typography>}
    </div>
    {children}
  </Stack>
);

/** A read-only value in a field-shaped box, which is what `.offer-info-box` draws. */
const InfoField = ({ label, children }) => (
  <>
    <FormLabel sx={LABEL_SX}>{label}</FormLabel>
    <div className='offer-info-box'>{children}</div>
  </>
);

const PickerField = ({ name, label, data, disabled, link }) => (
  <div>
    <FormLabel htmlFor={name} sx={LABEL_SX}>
      {label}
    </FormLabel>
    <Field
      name={name}
      id={name}
      component={renderComboBox}
      data={data}
      disabled={disabled}
      valueField='id'
      textField='name'
      link={link}
    />
  </div>
);

const OfferProperties = ({ selectedElement, containers, layouts, offerVariationsByPrimaryOfferId }) => {
  const { data: offerBlock } = useOfferBlockByOfferId(!!selectedElement?.offer?.id, selectedElement?.offer?.id);

  const { id, offerType, name, offer, offersSequence } = useMemo(() => selectedElement ?? {}, [selectedElement]);

  const filteredContainers = useMemo(() => filterContainersByOfferType(containers, offerType), [containers, offerType]);

  const isOfferBlock = useMemo(() => offerType?.name === 'Offer Block', [offerType?.name]);

  // ZET-4795: hidden wrapper offer for a linkout-family offer action
  const isDestination = useMemo(() => offerType?.name === DESTINATION_OFFER_TYPE_NAME, [offerType?.name]);

  // Carried on the offers/summaries projection — no extra fetch to identify the action
  const offerActionId = offer?.offerActionId ?? offer?.offerAction?.id;
  const offerActionType = offer?.offerActionType ?? offer?.offerAction?.offerActionType;

  const sequenceId = offersSequence?.id ? offersSequence.id : `This OS hasn't been created yet`;

  // The same lookup the flow node makes for its live/paused counts, so the card lists exactly those.
  const offerVariations = offer?.isPrimaryVariant ? offerVariationsByPrimaryOfferId?.[offer.id] : null;

  if (!id) return null;

  if (isOfferBlock) {
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <PropertiesHeader name={name} subtitle={`Offer Block ID: ${offerBlock?.id}`}>
          <VisitLink to={`/campaigns/offerblocks/${offerBlock?.id}`} title='Visit Offer Details' />
        </PropertiesHeader>
        {offerBlock?.offerBlockOffers?.map((offerBlockOffer) => {
          const { offer: blockOffer } = offerBlockOffer;
          return (
            <Link
              to={`/campaigns/offertable/offer/${blockOffer?.id}`}
              target='_blank'
              rel='noopener noreferrer'
              data-external-link='true'
              key={blockOffer?.id}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Box className='offer-block-item'>
                <Typography variant='h6'>{`${blockOffer?.id} : ${blockOffer?.name}`}</Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid size={6}>
                    <FormLabel sx={LABEL_SX}>Offer Type</FormLabel>
                    <TextField value={blockOffer?.offerType?.name ?? ''} disabled />
                  </Grid>
                  <Grid size={6}>
                    <FormLabel sx={LABEL_SX}>Advertiser</FormLabel>
                    <TextField value={blockOffer?.advertiser?.name ?? ''} disabled />
                  </Grid>
                </Grid>
              </Box>
            </Link>
          );
        })}
      </Stack>
    );
  }

  // ZET-4795: a Destination node is position-only. Everything about it — URL, payout,
  // criteria, waterfall entries, pre-redirect message — lives on the Offer Action, so this
  // pane is read-only identity plus a link out to that screen. No container/layout fields:
  // the node renders a redirect, never a template.
  if (isDestination) {
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <PropertiesHeader name={name}>
          {/* offerActionId rides along on the offers/summaries projection (ZET-4795), so the
              deep link costs no extra request. Older saved nodes hydrated from a DTO without
              it fall back to the Offer Actions list. */}
          <VisitLink
            to={offerActionId ? `/campaigns/offeractions/${offerActionId}` : '/campaigns/offeractions'}
            title='Visit Offer Action Details'
          />
        </PropertiesHeader>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant='subtitle2'>
              <em>Offer Action ID: </em>
              {offerActionId ?? '—'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant='subtitle2'>
              <em>Offer Sequence ID: </em>
              {sequenceId}
            </Typography>
          </Grid>
          <Grid size={6}>
            <InfoField label='Action Type'>{offerActionType ?? offerType?.name}</InfoField>
          </Grid>
          <Grid size={6}>
            {/* Mirrored from the offer action for bare LINKOUTs only; waterfalls span many
                destinations, so they legitimately have none. */}
            <InfoField label='Advertiser'>
              {offer?.advertiser?.name ? offer.advertiser.name : 'No Advertiser'}
            </InfoField>
          </Grid>
        </Grid>
        <Typography component='em' variant='body2'>
          Destinations are configured on the Offer Actions screen. Only their position in the sequence is set here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <PropertiesHeader name={name}>
        <VisitLink to={`/campaigns/offertable/offer/${offer?.id}`} title='Visit Offer Details' />
      </PropertiesHeader>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant='subtitle2'>
            <em>Offer ID: </em>
            {offer?.id}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant='subtitle2'>
            <em>Offer Sequence ID: </em>
            {sequenceId}
          </Typography>
        </Grid>
        <Grid size={6}>
          <InfoField label='Offer Type'>{offerType?.name}</InfoField>
        </Grid>
        <Grid size={6}>
          <InfoField label='Advertiser'>{offer?.advertiser?.name ? offer.advertiser.name : 'N/A'}</InfoField>
        </Grid>
      </Grid>
      <OfferVariationsCard variations={offerVariations} />
      <PickerField name={`${id}.layout`} label='Layout' data={layouts} disabled={!id} link='/content/layouts' />
      <PickerField
        name={`${id}.container`}
        label='Container'
        data={filteredContainers}
        disabled={!id}
        link='/content/containers'
      />
    </Stack>
  );
};

export default OfferProperties;
